import { FlexBox } from "components/Common/FlexBox"
import {
  StyledNutritionRemaining,
  StyledNutritionItem,
  NutritionName,
  NutritionValue,
  NutritionGoalValue,
  ProgressTrackBar,
} from "./styled"
import { ProgressBar } from "antd-mobile"
import { formatDisplayNumber, roundByDecimalPlace } from "utils"
import { useAppSelector } from "app/hooks"
import CountUp from "react-countup"
import { useDiaryAnimationLoading } from "features/home/hooks"
import { useEffect, useState } from "react"

const getDisplayPercent = (key: string, dailyProgress?: any) => {
  if (!dailyProgress?.goal) return 0

  const gainedNutrition = dailyProgress.pfc[key]
  const nutritionPerDay = dailyProgress.goal.pfc[key]
  const isOver = gainedNutrition > nutritionPerDay

  let result = 0

  if (isOver) {
    result = 100
  } else {
    result = (gainedNutrition / nutritionPerDay) * 100
  }

  result = result > 0 ? result : 0

  return roundByDecimalPlace(result, 0)
}

const RemainingNutrition = () => {
  const dailyProgress = useAppSelector((state) => state.home.dailyProgress)
  const getDailyProgressCounter = useAppSelector(
    (state) => state.home.getDailyProgressCounter,
  )
  const loading = useAppSelector((state) => state.home.getDailyProgressLoading)
  const localLoading = useDiaryAnimationLoading(getDailyProgressCounter)
  const animationLoading = localLoading || loading

  const remainingFat = dailyProgress?.goal
    ? dailyProgress?.goal.pfc.fat - dailyProgress?.pfc?.fat
    : 0
  const remainingProtein = dailyProgress?.goal
    ? dailyProgress?.goal.pfc.protein - dailyProgress?.pfc?.protein
    : 0
  const remainingCarb = dailyProgress?.goal
    ? dailyProgress?.goal.pfc.carb - dailyProgress?.pfc?.carb
    : 0

  const goalProteinGram = dailyProgress?.goal?.pfc.protein || 0
  const goalFatGram = dailyProgress?.goal?.pfc.fat || 0
  const goalCarbGram = dailyProgress?.goal?.pfc.carb || 0

  const [persistentProteinGram, setPersistentProteinGram] =
    useState(goalProteinGram)
  const [persistentFatGram, setPersistentFatGram] = useState(goalFatGram)
  const [persistentCarbGram, setPersistentCarbGram] = useState(goalCarbGram)

  useEffect(() => {
    if (goalProteinGram !== 0) {
      setPersistentProteinGram(goalProteinGram)
    }
    if (goalFatGram !== 0) {
      setPersistentFatGram(goalFatGram)
    }
    if (goalCarbGram !== 0) {
      setPersistentCarbGram(goalCarbGram)
    }
  }, [goalProteinGram, goalFatGram, goalCarbGram])

  const nutrition = [
    {
      name: "Protein",
      value: roundByDecimalPlace(dailyProgress?.pfc?.protein ?? 0, 0),
      isOver: remainingProtein < 0,
      color: "var(--Accent-2, #F354E4)",
      percent: getDisplayPercent("protein", dailyProgress),
      goalValue: formatDisplayNumber(persistentProteinGram),
    },
    {
      name: "Fat",
      value: roundByDecimalPlace(dailyProgress?.pfc?.fat ?? 0, 0),
      isOver: remainingFat < 0,
      color: "var(--Schemes-Secondary, #FFC800)",
      percent: getDisplayPercent("fat", dailyProgress),
      goalValue: formatDisplayNumber(persistentFatGram),
    },
    {
      name: "Carbs",
      value: roundByDecimalPlace(dailyProgress?.pfc?.carb ?? 0, 0),
      isOver: remainingCarb < 0,
      color: "var(--Accent-1, #8948FA)",
      percent: getDisplayPercent("carb", dailyProgress),
      goalValue: formatDisplayNumber(persistentCarbGram),
    },
  ]

  return (
    <StyledNutritionRemaining>
      {nutrition.map((item: any) => {
        return (
          <StyledNutritionItem key={item.name}>
            <FlexBox
              $alignItems="flex-end"
              $justify="space-between"
              style={{ height: "17px" }}
            >
              <NutritionName> {item.name}</NutritionName>

              <FlexBox $alignItems="center" $gap={2} style={{ height: "17px" }}>
                <NutritionValue>
                  {" "}
                  <span
                    className={`loading-value ${
                      animationLoading && "loading-value--show"
                    }`}
                  >
                    -
                  </span>
                  <span
                    className={`real-value ${
                      animationLoading && "real-value--hidden"
                    }`}
                  >
                    <CountUp
                      separator={","}
                      style={{ display: "inline-block" }}
                      end={item.value}
                      duration={0.5}
                    ></CountUp>
                  </span>
                </NutritionValue>
                <NutritionGoalValue>/{item.goalValue}g</NutritionGoalValue>
              </FlexBox>
            </FlexBox>

            <div style={{ position: "relative" }}>
              <ProgressTrackBar />

              <ProgressBar
                rounded={item.percent === 0 ? false : true}
                percent={item.percent}
                style={{
                  "--track-width": "4px",
                  "--fill-color": item.isOver ? "#FB6D6D" : item.color,
                  "--track-color": "transparent",
                  zIndex: 2,
                  position: "relative",
                }}
              />
            </div>
          </StyledNutritionItem>
        )
      })}
    </StyledNutritionRemaining>
  )
}

export default RemainingNutrition
